import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import type { ErrorCode, IssueDetail, IssueFieldOptions } from '@jisy/shared';
import { ApiRequestError } from '../api/client.js';
import { issueChildrenQuery, useIssueCreate } from '../api/queries.js';
import { Avatar, StatusBadge } from './Badges.js';
import styles from './IssueChildren.module.css';

/**
 * Раздел «Подзадачи» карточки — у задачи её подзадачи, у эпика его задачи, как в Jira:
 * сколько готово, список со статусами и строка быстрого создания.
 *
 * Строка создания спрашивает только название и тип — ровно то, что спрашивает сама Jira.
 * Исполнитель, срок и описание правятся уже в карточке созданной: форма на все поля ради
 * подзадачи «Прогнать тесты» была бы тяжелее самой подзадачи. Строка не закрывается после
 * создания — подзадачи обычно заводят пачкой, одну за другой.
 */
export function IssueChildren({
  issue,
  issueTypes,
  siteUrl,
  onOpenIssue,
}: {
  issue: IssueDetail;
  /** Типы проекта из значений карточки; null — ещё не пришли. */
  issueTypes: IssueFieldOptions['issueTypes'] | null;
  siteUrl: string;
  onOpenIssue: (issueKey: string) => void;
}): React.ReactElement | null {
  const { t } = useTranslation();

  const level = issue.type?.hierarchyLevel ?? 0;
  // Ребёнок стоит ровно на уровень ниже: у задачи — типы подзадач, у эпика — типы задач.
  const childTypes = useMemo(
    () => (issueTypes ?? []).filter((type) => type.hierarchyLevel === level - 1),
    [issueTypes, level],
  );

  const children = useQuery(issueChildrenQuery(issue.key, true));
  const create = useIssueCreate();

  const [adding, setAdding] = useState(false);
  const [summary, setSummary] = useState('');
  const [typeId, setTypeId] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Тип подставляется сразу: у большинства проектов он один, и выбирать его незачем.
  useEffect(() => {
    const first = childTypes[0];
    if (typeId === '' && first) setTypeId(first.id);
  }, [childTypes, typeId]);

  const items = children.data?.items ?? [];
  const done = items.filter((child) => child.status?.category === 'done').length;

  // Детей нет и завести их нечем — раздел ничего не скажет и ничего не даст сделать.
  if (items.length === 0 && childTypes.length === 0 && !children.isError) return null;

  const subtasks = level <= 0;
  const error = create.error instanceof ApiRequestError ? create.error : null;

  function submit(): void {
    const text = summary.trim();
    if (text === '' || typeId === '' || create.isPending) return;

    create.mutate(
      { projectId: issue.projectId, issueTypeId: typeId, parentKey: issue.key, summary: text },
      {
        onSuccess: () => {
          setSummary('');
          inputRef.current?.focus();
        },
      },
    );
  }

  function cancel(): void {
    setAdding(false);
    setSummary('');
    create.reset();
  }

  return (
    <section className={styles.section}>
      <div className={styles.head}>
        <span className="label">
          {t(subtasks ? 'children.subtasks' : 'children.childIssues', { count: items.length })}
        </span>
        {childTypes.length > 0 && !adding && (
          <button
            type="button"
            className={styles.linkButton}
            onClick={() => {
              setAdding(true);
              create.reset();
            }}
          >
            {t(subtasks ? 'children.addSubtask' : 'children.addChild')}
          </button>
        )}
      </div>

      {items.length > 0 && (
        <div className={styles.progress}>
          <div
            className={styles.progressTrack}
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={items.length}
            aria-valuenow={done}
            aria-label={t('children.progress', { done, total: items.length })}
          >
            <div
              className={styles.progressFill}
              style={{ width: `${Math.round((done / items.length) * 100)}%` }}
            />
          </div>
          <span className={`mono ${styles.progressText}`}>
            {t('children.progress', { done, total: items.length })}
          </span>
        </div>
      )}

      {items.length > 0 && (
        <ul className={styles.list}>
          {items.map((child) => (
            <li key={child.key}>
              <button
                type="button"
                className={styles.item}
                onClick={() => onOpenIssue(child.key)}
                title={child.summary}
              >
                <span className={styles.typeGlyph} aria-hidden="true">
                  {child.type?.name.charAt(0).toUpperCase() ?? '·'}
                </span>
                <span className={`mono ${styles.key}`}>{child.key}</span>
                <span className={`${styles.summary} truncate`}>{child.summary}</span>
                <span className={styles.assignee}>
                  <Avatar user={child.assignee} size={20} />
                  <span className="visually-hidden">
                    {child.assignee?.displayName ?? t('table.unassigned')}
                  </span>
                </span>
                <span className={styles.status}>
                  <StatusBadge status={child.status} />
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {children.data?.truncated && (
        <a
          className={styles.more}
          href={`${siteUrl}/browse/${issue.key}`}
          target="_blank"
          rel="noreferrer noopener"
        >
          {t('children.truncated', { count: items.length })}
        </a>
      )}

      {children.isError && (
        <p className={styles.error} role="alert">
          {t('children.loadFailed')}
        </p>
      )}

      {adding && (
        // Esc здесь закрывает строку создания, а не всю карточку: окно смотрит на эту метку.
        <div className={styles.create} data-escape-local="">
          {childTypes.length > 1 && (
            <select
              className={styles.select}
              value={typeId}
              onChange={(event) => setTypeId(event.target.value)}
              aria-label={t('table.columns.type')}
            >
              {childTypes.map((type) => (
                <option value={type.id} key={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
          )}
          <input
            ref={inputRef}
            className={styles.input}
            value={summary}
            onChange={(event) => setSummary(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                submit();
              } else if (event.key === 'Escape') {
                event.preventDefault();
                cancel();
              }
            }}
            placeholder={t('create.summaryPlaceholder')}
            aria-label={t('table.columns.summary')}
            maxLength={255}
            autoFocus
          />
          <button
            type="button"
            className={styles.primaryButton}
            onClick={submit}
            disabled={create.isPending || summary.trim() === '' || typeId === ''}
          >
            {create.isPending ? t('create.creating') : t('create.submit')}
          </button>
          <button type="button" className={styles.secondaryButton} onClick={cancel}>
            {t('issue.cancel')}
          </button>
        </div>
      )}

      {adding && error && (
        <p className={styles.error} role="alert">
          {error.code === 'JIRA_REQUIRED_FIELDS'
            ? t('create.requiredBody', { fields: String(error.params?.['fields'] ?? '') })
            : t(`errors.${error.code}` satisfies `errors.${ErrorCode}`)}
        </p>
      )}
    </section>
  );
}
